import { nextRowKey } from './model'

export function createPathSegment(existingSegments, element = '') {
  return { key: nextRowKey(), element, reverse: false }
}

export const DEFAULT_PATH = { segments: [], sampling: { mode: 'samples', samples: 21, step: '' } }

export function createTrainAxle(existingAxles) {
  return { key: nextRowKey(), offset: existingAxles.length === 0 ? 0 : '', value: -75 }
}

export function createTrain(existingTrains) {
  return {
    key: nextRowKey(),
    id: `TR${existingTrains.length + 1}`,
    name: '',
    component: 'Fy',
    coordinateSystem: 'global',
    axles: [{ key: nextRowKey(), offset: 0, value: -75 }],
    length: '',
    insideDistributedLoad: '',
    outsideDistributedLoad: '',
  }
}

export const DEFAULT_TRAINS = []

// The three internal-force diagrams are always requested together — the product decided the
// user shouldn't have to configure a target/load per response, only path + trains.
export const MOVING_RESPONSES = ['axial', 'shear', 'moment']

export function cascadeRemoveTrain(trainKey, { trains, combinations = [] }) {
  const removedTrain = trains.find((t) => t.key === trainKey)
  const nextTrains = trains.filter((t) => t.key !== trainKey)
  if (!removedTrain) return { trains: nextTrains, combinations }

  const nextCombinations = combinations.map((c) => {
    if (!(removedTrain.id in (c.trainFactors || {}))) return c
    const trainFactors = { ...c.trainFactors }
    delete trainFactors[removedTrain.id]
    return { ...c, trainFactors }
  })

  return { trains: nextTrains, combinations: nextCombinations }
}

function mapSamplingForApi(sampling) {
  if (sampling?.mode === 'step' && sampling.step !== '' && sampling.step !== undefined) {
    return { step: Number(sampling.step) }
  }
  return { samples: Number(sampling?.samples) || 21 }
}

export function mapPathForApi(path) {
  return {
    segments: path.segments.map((s) => ({ element: s.element, reverse: !!s.reverse })),
    sampling: mapSamplingForApi(path.sampling),
  }
}

export function mapTrainsForApi(trains) {
  return trains.map((t) => {
    const train = {
      id: t.id,
      name: t.name || undefined,
      component: t.component,
      coordinateSystem: t.coordinateSystem,
      axles: t.axles.map((a) => ({ offset: Number(a.offset) || 0, value: Number(a.value) || 0 })),
    }
    if (t.length !== '' && t.length !== undefined && t.length !== null) {
      train.length = Number(t.length)
    }
    if (t.insideDistributedLoad !== '' && t.insideDistributedLoad !== undefined && t.insideDistributedLoad !== null) {
      train.insideDistributedLoad = { value: Number(t.insideDistributedLoad) }
    }
    if (
      t.outsideDistributedLoad !== '' &&
      t.outsideDistributedLoad !== undefined &&
      t.outsideDistributedLoad !== null
    ) {
      train.outsideDistributedLoad = { value: Number(t.outsideDistributedLoad) }
    }
    return train
  })
}

export function validateMovingLoads({ path, trains, elementIds }) {
  const errors = []

  if (path.segments.length === 0) {
    errors.push('Cargas móveis: adicione ao menos um segmento ao caminho')
  }
  path.segments.forEach((s, index) => {
    if (!elementIds.includes(s.element)) errors.push(`Caminho: segmento #${index + 1} tem elemento inválido`)
  })

  if (trains.length === 0) {
    errors.push('Cargas móveis: adicione ao menos um trem-tipo')
  }

  const seenTrainIds = new Set()
  trains.forEach((t, index) => {
    const label = t.id || `#${index + 1}`
    if (t.id === '') errors.push(`Trem #${index + 1}: id obrigatório`)
    else if (seenTrainIds.has(t.id)) errors.push(`Trem "${t.id}" duplicado`)
    else seenTrainIds.add(t.id)

    const hasDistributedLoad = t.insideDistributedLoad !== '' || t.outsideDistributedLoad !== ''
    if (t.axles.length === 0 && !hasDistributedLoad) {
      errors.push(`Trem "${label}": adicione ao menos um eixo ou uma carga distribuída`)
    }
    t.axles.forEach((a, axleIndex) => {
      if (a.offset === '' || isNaN(Number(a.offset)) || Number(a.offset) < 0) {
        errors.push(`Trem "${label}": eixo #${axleIndex + 1} tem offset inválido`)
      }
      if (a.value === '' || isNaN(Number(a.value))) {
        errors.push(`Trem "${label}": eixo #${axleIndex + 1} tem valor inválido`)
      }
    })

    if (hasDistributedLoad && (t.length === '' || isNaN(Number(t.length)) || Number(t.length) <= 0)) {
      errors.push(`Trem "${label}": informe o comprimento do trem para usar carga distribuída`)
    }
  })

  return errors
}
